import { Link } from 'react-router-dom';
import { formatCurrency, formatDate } from '../../lib/format';
import type { InvoiceDetail } from '../../lib/invoices';

interface CreditfacturenSectionProps {
  credit: InvoiceDetail['invoice']['credit'];
  /** Sluit de drawer als de gebruiker naar een gelinkte factuur navigeert. */
  onNavigate?: () => void;
}

/**
 * Toont de credit-relaties van een factuur: op een origineel de lijst
 * creditfacturen, op een creditfactuur de verwijzing terug naar het origineel
 * (die laatste staat ook als badge in de header — hier met context).
 */
export function CreditfacturenSection({ credit, onNavigate }: CreditfacturenSectionProps) {
  if (!credit.isCreditfactuur && credit.creditInvoices.length === 0) return null;

  return (
    <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-4">
      <h2 className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-2">
        {credit.isCreditfactuur ? 'Creditfactuur bij' : 'Creditfacturen'}
      </h2>

      {credit.isCreditfactuur ? (
        credit.creditedInvoiceId ? (
          <Link
            to={`/facturen/${credit.creditedInvoiceId}`}
            onClick={onNavigate}
            className="text-sm text-indigo-600 hover:underline"
          >
            Factuur {credit.creditedInvoiceName || credit.creditedInvoiceId} →
          </Link>
        ) : (
          <p className="text-sm text-slate-400 italic">Origineel onbekend</p>
        )
      ) : (
        <ul className="divide-y divide-slate-100">
          {credit.creditInvoices.map((c) => (
            <li key={c.id} className="py-2 first:pt-0 last:pb-0 flex items-center gap-3 text-sm">
              <Link
                to={`/facturen/${c.id}`}
                onClick={onNavigate}
                className="text-indigo-600 hover:underline flex-shrink-0"
              >
                {c.name}
              </Link>
              <span className="text-xs text-slate-400 truncate flex-1">
                {formatDate(c.invoiceDate)}
              </span>
              {c.openAmount < -0.005 && (
                <span
                  className="text-[10px] px-1.5 py-0.5 rounded font-medium bg-amber-100 text-amber-700 flex-shrink-0"
                  title="Er staat nog een bedrag open om terug te betalen"
                >
                  Terugbetaling open
                </span>
              )}
              <span className="tabular-nums text-slate-700 flex-shrink-0">
                {formatCurrency(c.totalInclBtw)}
              </span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
